import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import './EmptyState.css';

/**
 * EmptyState — "nothing here yet" (an empty cart, no orders, no saved
 * designs, a shop with no products, no FAQ hits): a large outline icon (or a
 * brand's own `art`), a title, a line of copy and the way out as DS Buttons.
 *
 * `size="page"` (default) is the centred page state — H2 title, 64px icon,
 * medium-m rhythm, the block stretches to centre itself in a flex column
 * (the brand carts, order pages, closed shops); `size="drawer"` the compact
 * one for drawers and panels — H3 title; `size="inline"` the text-only one
 * inside a list (FAQ "no results") — H3 + copy, no icon well, no actions.
 * The heading level follows the size; `headingLevel` overrides it where the
 * page's outline needs another. `--ds-empty-state-message-max` (400px) caps
 * the page message width.
 */
const EmptyState = ({
  icon: Icon,
  art,
  title,
  message,
  actions = [],
  size = 'page',
  headingLevel,
  className = '',
}) => {
  const inline = size === 'inline';
  const level = headingLevel || (size === 'page' ? 2 : 3);
  const Heading = `h${level}`;
  const headingClass = size === 'page' ? 'typo-h2' : 'typo-h3';

  return (
    <div className={['ds-empty-state', `ds-empty-state--${size}`, className].filter(Boolean).join(' ')}>
      {!inline && (art || (Icon && (
        <span className="ds-empty-state__icon" aria-hidden="true">
          {React.isValidElement(Icon) ? Icon : <Icon strokeWidth={1.5} />}
        </span>
      )))}
      <div className="ds-empty-state__copy">
        {title && <Heading className={`ds-empty-state__title ${headingClass}`}>{title}</Heading>}
        {message && <p className="ds-empty-state__message typo-body">{message}</p>}
      </div>
      {!inline && actions.length > 0 && (
        <div className="ds-empty-state__actions">
          {actions.map(({ label, onClick, href, variant = 'main', size: buttonSize = 'secondary', iconRight, iconLeft, className: buttonClass }) => (
            <Button
              key={label}
              size={buttonSize}
              variant={variant}
              href={href}
              onClick={onClick}
              iconRight={iconRight}
              iconLeft={iconLeft}
              className={buttonClass}
            >
              {label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

EmptyState.propTypes = {
  /** Outline icon — a lucide component (drawn large) or an element */
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  /** Replaces the icon well with the brand's own illustration */
  art: PropTypes.node,
  /** Omit it for a bare waiting line ("Loading your order…") */
  title: PropTypes.node,
  message: PropTypes.node,
  /** The way out: DS Buttons — [{ label, onClick, href, variant, size, iconRight, iconLeft, className }]; the first is the primary */
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    href: PropTypes.string,
    variant: PropTypes.string,
    size: PropTypes.string,
    iconRight: PropTypes.node,
    iconLeft: PropTypes.node,
    className: PropTypes.string,
  })),
  /** 'page' — centred page state (H2, 64px icon) · 'drawer' — compact (H3) · 'inline' — text only inside a list (H3, no icon, no actions) */
  size: PropTypes.oneOf(['page', 'drawer', 'inline']),
  /** Heading level override (2 / 3 by size) */
  headingLevel: PropTypes.oneOf([1, 2, 3, 4]),
  className: PropTypes.string,
};

export default EmptyState;
